import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { db, functions } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useOrganization } from '../contexts/OrganizationContext';
import { useModule } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { candidateExclusionClauses } from '../lib/filters/contactFilters';
import { isSoftDeleted } from '../lib/appsheet/softDelete';
import { mapFirestoreToDataHubContact } from './useDataHubContacts';
import { leadKeys } from './useLeads';
import type { Contact, Lead } from '../types/lead';

// Query key factory
//
// `canViewCandidates` is threaded into every list/search/company key so a P14
// (Recruiting) user's candidate-inclusive results never cache-bleed to a non-P14
// user, and vice versa (#2559). Defaults keep the keys stable for existing callers.
export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>, canViewCandidates = false) =>
    [...contactKeys.lists(), filters, canViewCandidates] as const,
  search: (term: string, canViewCandidates = false) =>
    [...contactKeys.lists(), 'search', term, canViewCandidates] as const,
  byCompany: (companyName: string, canViewCandidates = false) =>
    [...contactKeys.lists(), 'company', companyName, canViewCandidates] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactKeys.details(), id] as const,
  interactions: (phone?: string, email?: string) => [...contactKeys.all, 'interactions', { phone, email }] as const,
};

/**
 * Map one appsheet_shared_contacts doc onto the unified Contact shape (#4586).
 *
 * The mirror stores AppSheet column names verbatim ("First Name", "Primary
 * Email", ...), read here through the same mapper useDataHubContacts uses.
 * camelCase keys stay as a fallback for any legacy doc written before the
 * bracketed mirror, so neither shape reads as a blank "Unknown" contact.
 */
function appSheetDocToContact(
  docId: string,
  data: Record<string, unknown>,
  orgId: string
): Contact {
  const mirror = mapFirestoreToDataHubContact(docId, data);
  const legacy = (key: string): string =>
    typeof data[key] === 'string' ? (data[key] as string) : '';

  const firstName = mirror.firstName || legacy('firstName');
  const lastName = mirror.lastName || legacy('lastName');
  const fullName = `${firstName} ${lastName}`.trim() || mirror.computedName.trim();
  const lastModified: Timestamp =
    data.lastModified instanceof Timestamp ? data.lastModified : Timestamp.now();

  return {
    contactId: docId,
    organizationId: orgId,
    firstName,
    lastName,
    fullName: fullName || 'Unknown',
    email: mirror.primaryEmail || legacy('primaryEmail') || undefined,
    phone: mirror.primaryPhone || legacy('primaryPhone') || undefined,
    companyName: mirror.company || legacy('company') || undefined,
    title: mirror.role || legacy('role') || undefined,
    source: 'appsheet_sync',
    createdAt: lastModified,
    updatedAt: lastModified,
    createdBy: 'appsheet',
  } as Contact;
}

/**
 * The spellings of a company name to ask Firestore for.
 *
 * AppSheet's "Company" is matched by equality (Firestore has no
 * case-insensitive filter), so the picker asks for the name as the report has
 * it plus its trimmed, lower, UPPER and Title Case forms, then keeps only the
 * rows whose Company matches case-insensitively. Measured 2026-09-29 against
 * production: of 72 approved FSRs whose customer has AppSheet contacts, 71
 * match exactly and 1 differs only by case ("Trident property management" vs
 * "Trident Property Management"), which the Title Case form catches. A mixed
 * spelling none of these forms produce (e.g. "McCoy" typed "MCcoy") is not
 * found; that is the known limit of an indexed equality read.
 */
export function companyNameVariants(companyName: string): string[] {
  const trimmed = companyName.trim();
  const lower = trimmed.toLowerCase();
  const title = lower
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return Array.from(new Set([companyName, trimmed, lower, trimmed.toUpperCase(), title]))
    .filter((v) => v.length > 0);
}

// ============================================
// CONTACT QUERIES
// ============================================

/**
 * Search contacts with typeahead.
 *
 * Searches the unified `contacts` collection ONLY. It does not query
 * appsheet_shared_contacts, so a contact that exists only in the AppSheet
 * mirror (not yet bridged into `contacts`) will not appear here. (This comment
 * previously claimed both collections were searched; the code never did.)
 */
export function useSearchContacts(searchTerm: string, maxResults: number = 10) {
  const { currentOrganization } = useOrganization();
  // Recruiting candidates must not surface in the general contact typeahead for
  // users without P14 (Recruiting) access (#2559).
  const { hasModuleAccess } = useModule();
  const canViewCandidates = hasModuleAccess('P14');

  return useQuery({
    queryKey: contactKeys.search(searchTerm, canViewCandidates),
    queryFn: async (): Promise<Contact[]> => {
      if (!currentOrganization?.organizationId || !searchTerm || searchTerm.length < 2) {
        return [];
      }

      const orgId = currentOrganization.organizationId;
      const term = searchTerm.toLowerCase();
      const digits = searchTerm.replace(/\D/g, '');
      const isPhoneSearch = digits.length >= 3;

      // Positive `isCandidate == false` equality appended to every sub-query for
      // non-P14 users (#2559). Each range sub-query needs a composite index that
      // adds isCandidate before the ranged field (see firestore.indexes.json).
      const candidateClauses = candidateExclusionClauses(canViewCandidates);

      // Build targeted queries based on search type
      const queries: Promise<Contact[]>[] = [];

      if (isPhoneSearch) {
        // Phone prefix search on normalizedPhone (indexed, fast)
        const phonePrefix = digits.length > 10 ? digits.slice(-10) : digits;
        const phoneEnd = phonePrefix.slice(0, -1) + String.fromCharCode(phonePrefix.charCodeAt(phonePrefix.length - 1) + 1);
        queries.push(
          getDocs(query(
            collection(db, COLLECTIONS.CONTACTS),
            where('organizationId', '==', orgId),
            ...candidateClauses,
            where('normalizedPhone', '>=', phonePrefix),
            where('normalizedPhone', '<', phoneEnd),
            limit(maxResults),
          )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
        );
      }

      // Name/email search — use searchName (lowercased) for case-insensitive prefix match
      if (!isPhoneSearch || digits.length < 7) {
        const termEnd = term.slice(0, -1) + String.fromCharCode(term.charCodeAt(term.length - 1) + 1);

        // Primary: searchName prefix (lowercased, case-insensitive)
        queries.push(
          getDocs(query(
            collection(db, COLLECTIONS.CONTACTS),
            where('organizationId', '==', orgId),
            ...candidateClauses,
            where('searchName', '>=', term),
            where('searchName', '<', termEnd),
            limit(maxResults),
          )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
          .catch(() => []) // searchName may not exist on older contacts yet
        );

        // Fallback: fullName prefix (capitalized — for contacts without searchName)
        const capitalizedTerm = term.charAt(0).toUpperCase() + term.slice(1);
        const capitalizedEnd = capitalizedTerm.slice(0, -1) + String.fromCharCode(capitalizedTerm.charCodeAt(capitalizedTerm.length - 1) + 1);
        queries.push(
          getDocs(query(
            collection(db, COLLECTIONS.CONTACTS),
            where('organizationId', '==', orgId),
            ...candidateClauses,
            where('fullName', '>=', capitalizedTerm),
            where('fullName', '<', capitalizedEnd),
            limit(maxResults),
          )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
        );

        // Also try UPPERCASE (many legacy names are UPPERCASE)
        const upperTerm = searchTerm.toUpperCase();
        const upperEnd = upperTerm.slice(0, -1) + String.fromCharCode(upperTerm.charCodeAt(upperTerm.length - 1) + 1);
        queries.push(
          getDocs(query(
            collection(db, COLLECTIONS.CONTACTS),
            where('organizationId', '==', orgId),
            ...candidateClauses,
            where('fullName', '>=', upperTerm),
            where('fullName', '<', upperEnd),
            limit(maxResults),
          )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
        );

        // Email prefix match
        if (term.includes('@') || term.includes('.')) {
          const emailEnd = term.slice(0, -1) + String.fromCharCode(term.charCodeAt(term.length - 1) + 1);
          queries.push(
            getDocs(query(
              collection(db, COLLECTIONS.CONTACTS),
              where('organizationId', '==', orgId),
              ...candidateClauses,
              where('normalizedEmail', '>=', term),
              where('normalizedEmail', '<', emailEnd),
              limit(maxResults),
            )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
          );
        }

        // Company name prefix (lowercased via searchName pattern)
        queries.push(
          getDocs(query(
            collection(db, COLLECTIONS.CONTACTS),
            where('organizationId', '==', orgId),
            ...candidateClauses,
            where('companyName', '>=', capitalizedTerm),
            where('companyName', '<', capitalizedEnd),
            limit(maxResults),
          )).then(snap => snap.docs.map(d => ({ ...d.data(), contactId: d.id } as Contact)))
          .catch(() => [])
        );
      }

      // Run all queries in parallel
      const allResults = await Promise.all(queries);

      // Deduplicate by contactId
      const seen = new Set<string>();
      const deduped: Contact[] = [];
      for (const batch of allResults) {
        for (const contact of batch) {
          if (!seen.has(contact.contactId)) {
            seen.add(contact.contactId);
            deduped.push(contact);
          }
        }
      }

      return deduped.slice(0, maxResults);
    },
    enabled: !!currentOrganization?.organizationId && searchTerm.length >= 2,
    staleTime: 1000 * 30, // 30 seconds for search
  });
}

/**
 * Fetch contacts by company name (the FSR "Email Report" recipient list).
 *
 * Searches both unified contacts and AppSheet shared contacts. The AppSheet
 * read is company-scoped (where Company in the name's spellings), so every
 * contact of that company is returned however large the mirror grows. It used
 * to read the org's first 500 AppSheet docs by id and filter them here, which
 * hid 208 of 262 AppSheet-only companies from the picker (#5088).
 */
export function useContactsByCompany(companyName?: string) {
  const { currentOrganization } = useOrganization();
  // Recruiting candidates must not appear in company-scoped contact lists for
  // users without P14 (Recruiting) access (#2559).
  const { hasModuleAccess } = useModule();
  const canViewCandidates = hasModuleAccess('P14');

  return useQuery({
    // Keyed on the trimmed name, matching the query: " Acme " and "Acme" share one cache entry.
    queryKey: contactKeys.byCompany(companyName?.trim() ?? '', canViewCandidates),
    queryFn: async (): Promise<Contact[]> => {
      if (!currentOrganization?.organizationId || !companyName?.trim()) {
        return [];
      }

      const orgId = currentOrganization.organizationId;
      const company = companyName.trim().toLowerCase();

      const [contactsSnap, appSheetSnap] = await Promise.all([
        getDocs(query(
          collection(db, COLLECTIONS.CONTACTS),
          where('organizationId', '==', orgId),
          ...candidateExclusionClauses(canViewCandidates),
          orderBy('fullName', 'asc'),
        )),
        // DASH-PERM: strict allow-list list rule requires a matching
        // where('organizationId') — an unconstrained list is denied outright.
        // organizationId + Company equality is served by the existing
        // (organizationId, Company) composite index in firestore.indexes.json.
        getDocs(query(
          collection(db, COLLECTIONS.APPSHEET_SHARED_CONTACTS),
          where('organizationId', '==', orgId),
          where('Company', 'in', companyNameVariants(companyName)),
        )),
      ]);

      const migratedAppSheetIds = new Set<string>();
      const results: Contact[] = [];

      contactsSnap.docs.forEach((d) => {
        const data = { ...d.data(), contactId: d.id } as Contact;
        if (data.source === 'appsheet_sync' && data.sourceId) {
          migratedAppSheetIds.add(data.sourceId);
        }
        if (data.companyName?.trim().toLowerCase() === company) {
          results.push(data);
        }
      });

      appSheetSnap.docs.forEach((d) => {
        if (migratedAppSheetIds.has(d.id)) return;
        const data = d.data();
        if (isSoftDeleted(data)) return;
        const contact = appSheetDocToContact(d.id, data, orgId);
        if (contact.companyName?.trim().toLowerCase() !== company) return;
        results.push(contact);
      });

      return results.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
    },
    enabled: !!currentOrganization?.organizationId && !!companyName?.trim(),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch a single contact by ID
 */
export function useContact(contactId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: contactKeys.detail(contactId || ''),
    queryFn: async (): Promise<Contact | null> => {
      if (!contactId) return null;

      const docRef = doc(db, COLLECTIONS.CONTACTS,contactId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return null;

      return {
        ...docSnap.data(),
        contactId: docSnap.id,
      } as Contact;
    },
    enabled: !!contactId,
    staleTime: 1000 * 60 * 5,
    initialData: () => {
      const lists = queryClient.getQueriesData<Contact[]>({ queryKey: contactKeys.lists() });
      for (const [, items] of lists) {
        if (!Array.isArray(items)) continue;
        const found = items?.find((c) => c.contactId === contactId);
        if (found) return found;
      }
      return undefined;
    },
    initialDataUpdatedAt: 0,
  });
}

// ============================================
// CONTACT MUTATIONS
// ============================================

/**
 * Create a new contact
 */
export function useCreateContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async (
      contactData: Omit<Contact, 'contactId' | 'organizationId' | 'fullName' | 'createdAt' | 'updatedAt' | 'createdBy'>
    ): Promise<string> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('No organization selected');
      }
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable<unknown, { contactId: string }>(functions, 'crm_dispatch');
      const result = await callable({
        action: 'createContact',
        ...contactData,
        organizationId: currentOrganization.organizationId,
      });
      return result.data.contactId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

/**
 * Update a contact
 */
export function useUpdateContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      contactId,
      updates,
    }: {
      contactId: string;
      updates: Partial<Omit<Contact, 'contactId' | 'organizationId' | 'createdAt' | 'createdBy'>>;
    }): Promise<void> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('Organization not loaded');
      }
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'updateContact', contactId, updates, organizationId: currentOrganization.organizationId });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ contactId, updates }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: contactKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Contact[]>({ queryKey: contactKeys.lists() });
      const previousDetail = queryClient.getQueryData<Contact>(contactKeys.detail(contactId));

      const now = Timestamp.now();

      // Calculate optimistic fullName if names are changing
      let optimisticFullName: string | undefined;
      if (updates.firstName !== undefined || updates.lastName !== undefined) {
        const currentContact = previousDetail;
        if (currentContact) {
          const firstName = updates.firstName ?? currentContact.firstName;
          const lastName = updates.lastName ?? currentContact.lastName;
          optimisticFullName = `${firstName} ${lastName}`.trim();
        }
      }

      const optimisticUpdates = {
        ...updates,
        ...(optimisticFullName ? { fullName: optimisticFullName } : {}),
        updatedAt: now,
      };

      // Optimistically update list caches
      queryClient.setQueriesData<Contact[]>({ queryKey: contactKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((contact) =>
          contact.contactId === contactId
            ? { ...contact, ...optimisticUpdates }
            : contact
        );
      });

      // Optimistically update detail cache
      if (previousDetail) {
        queryClient.setQueryData<Contact>(contactKeys.detail(contactId), {
          ...previousDetail,
          ...optimisticUpdates,
        });
      }

      return { previousLists, previousDetail };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(contactKeys.detail(variables.contactId), context.previousDetail);
      }
      toast.error('Failed to update contact');
    },
    onSettled: (_, __, variables) => {
      // Always revalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.contactId) });
    },
  });
}

/**
 * Delete a contact
 */
export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'deleteContact', contactId });
    },
    // Optimistic update for instant UI feedback
    onMutate: async (contactId) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: contactKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Contact[]>({ queryKey: contactKeys.lists() });
      const previousDetail = queryClient.getQueryData<Contact>(contactKeys.detail(contactId));

      // Optimistically remove from list caches
      queryClient.setQueriesData<Contact[]>({ queryKey: contactKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((contact) => contact.contactId !== contactId);
      });

      // Remove detail cache
      queryClient.removeQueries({ queryKey: contactKeys.detail(contactId) });

      return { previousLists, previousDetail };
    },
    onError: (_, contactId, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(contactKeys.detail(contactId), context.previousDetail);
      }
      toast.error('Failed to delete contact');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

/**
 * Link a contact to a lead (snapshot contact data to lead)
 */
export function useLinkContactToLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      contactId,
    }: {
      leadId: string;
      contactId: string;
    }): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'linkContactToLead', leadId, contactId });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ leadId, contactId }) => {
      // Cancel any in-flight lead detail queries
      await queryClient.cancelQueries({ queryKey: leadKeys.detail(leadId) });

      // Snapshot previous state for rollback
      const previousLead = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      // Optimistically set the linked contact as the lead's primary contact.
      // The server denormalizes name/email/phone onto the lead — mirror that
      // from the cached contact when we have it.
      if (previousLead) {
        const cachedContact = queryClient.getQueryData<Contact>(contactKeys.detail(contactId));
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...previousLead,
          primaryContactId: contactId,
          ...(cachedContact
            ? {
                primaryContactName: cachedContact.fullName || '',
                primaryContactEmail: cachedContact.email || '',
                primaryContactPhone: cachedContact.phone || '',
              }
            : {}),
        });
      }

      return { previousLead };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousLead) {
        queryClient.setQueryData(leadKeys.detail(variables.leadId), context.previousLead);
      }
      toast.error('Failed to link contact');
    },
    onSettled: (_, __, variables) => {
      // Always revalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

/**
 * Create contact and link to lead in one operation
 */
export function useCreateAndLinkContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async ({
      leadId,
      contactData,
    }: {
      leadId: string;
      contactData: Omit<Contact, 'contactId' | 'organizationId' | 'fullName' | 'createdAt' | 'updatedAt' | 'createdBy'>;
    }): Promise<string> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('No organization selected');
      }
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable<unknown, { contactId: string }>(functions, 'crm_dispatch');
      const result = await callable({
        action: 'createAndLinkContact',
        leadId,
        contactData,
        organizationId: currentOrganization.organizationId,
      });
      return result.data.contactId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      queryClient.invalidateQueries({ queryKey: ['leads', 'detail', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

